import { useMemo } from 'react';
import { styled } from '@mui/material/styles';

import { FONTS } from '../../shared/theme/typography';

/**
 * The scrolling sky behind a detailed day.
 *
 * The column runs 5am to midnight, and the gradient is anchored to the real
 * sun: a night-to-day transition centered on 6:15am and a day-to-night one
 * centered on 7:15pm, which is roughly sunrise and sunset in New York during
 * Climate Week. Watercolor clouds sit in the daylight band only.
 *
 * This is the same time-of-day metaphor as the card icons and the sidebar
 * filter, at a third scale — vertical position in the column reads as a time
 * before you look at a label.
 */

export const DAY_START_MIN = 5 * 60; // 5am
export const DAY_END_MIN = 24 * 60; // midnight
export const SPAN_MIN = DAY_END_MIN - DAY_START_MIN;
/**
 * Vertical pixels per hour. Tall enough that a one-hour event's card still fits
 * its title, tags and location without clipping, since blocks are positioned to
 * scale against the hour grid.
 */
export const HOUR_PX = 112;
export const COLUMN_HEIGHT = (SPAN_MIN / 60) * HOUR_PX;
/** The mini day's spacing, measured off "09.17 REFERENCE event callout main.jpg". */
export const MINI_HOUR_PX = 51;

const pct = (minutes: number) => ((minutes - DAY_START_MIN) / SPAN_MIN) * 100;

/** The sky at either end of the column, which a mini day centred near either continues past it. */
export const NIGHT_BEFORE = '#2b2c47';
export const NIGHT_AFTER = '#232438';

// Sunrise centered 6:15am, sunset centered 7:15pm.
const SKY_GRADIENT = `linear-gradient(180deg,
  ${NIGHT_BEFORE} 0%,
  #3b3a5c ${pct(5 * 60 + 20).toFixed(2)}%,
  #6a5570 ${pct(5 * 60 + 50).toFixed(2)}%,
  #b07a6b ${pct(6 * 60 + 5).toFixed(2)}%,
  #d0977f ${pct(6 * 60 + 15).toFixed(2)}%,
  #a9a6b0 ${pct(6 * 60 + 35).toFixed(2)}%,
  #7f9fc4 ${pct(7 * 60).toFixed(2)}%,
  #5f86b4 ${pct(9 * 60).toFixed(2)}%,
  #5c84b6 ${pct(14 * 60).toFixed(2)}%,
  #7e9dc0 ${pct(17 * 60).toFixed(2)}%,
  #b3a3ab ${pct(18 * 60 + 30).toFixed(2)}%,
  #d69a7d ${pct(19 * 60).toFixed(2)}%,
  #e0855f ${pct(19 * 60 + 15).toFixed(2)}%,
  #b5665f ${pct(19 * 60 + 40).toFixed(2)}%,
  #6d4f6b ${pct(20 * 60 + 20).toFixed(2)}%,
  #3d3557 ${pct(21 * 60).toFixed(2)}%,
  #2b2c47 ${pct(22 * 60).toFixed(2)}%,
  ${NIGHT_AFTER} 100%)`;

/**
 * Cloud placement. Fixed rather than random so the view is stable between
 * renders and between days — a sky that reshuffles as you page through days
 * reads as a bug. Positions and sizes follow the density in
 * "REFERENCE - calendar detailed day background.jpg".
 *
 * `opacity` here is a relative weight, scaled by CLOUD_OPACITY below. The
 * clouds sit behind event cards and hour rules, so they stay well back.
 */
const CLOUD_OPACITY = 0.3;

const CLOUDS: { top: number; left: number; width: number; opacity: number; flip?: boolean }[] = [
  { top: 8.5, left: -6, width: 46, opacity: 0.82 },
  { top: 13.5, left: 52, width: 54, opacity: 0.9, flip: true },
  { top: 21, left: 20, width: 62, opacity: 0.86 },
  { top: 27.5, left: 68, width: 44, opacity: 0.72, flip: true },
  { top: 34, left: -10, width: 40, opacity: 0.68 },
  { top: 41, left: 44, width: 58, opacity: 0.8 },
  { top: 49, left: 4, width: 50, opacity: 0.7, flip: true },
  { top: 56, left: 60, width: 46, opacity: 0.62 },
  { top: 63, left: 12, width: 54, opacity: 0.55 },
  { top: 69, left: 56, width: 42, opacity: 0.42, flip: true },
];

const Frame = styled('div')({
  position: 'absolute',
  inset: 0,
  overflow: 'hidden',
  borderRadius: 12,
  background: SKY_GRADIENT,
  pointerEvents: 'none',
});

const Cloud = styled('img')({
  position: 'absolute',
  userSelect: 'none',
});

const HourLine = styled('div')({
  position: 'absolute',
  left: 0,
  right: 0,
  borderTop: '1px dashed rgba(255,255,255,0.62)',
  pointerEvents: 'none',
});

const HourLabel = styled('div')(({ theme }) => ({
  position: 'absolute',
  // Sits in the Track's left padding, beside the column rather than inside it.
  left: -74,
  width: 62,
  textAlign: 'right',
  transform: 'translateY(-52%)',
  fontFamily: FONTS.serif,
  // Canela Deck Medium, per the design reference.
  fontWeight: 500,
  fontSize: 15,
  color: 'rgba(255,255,255,0.92)',
  textShadow: '0 1px 3px rgba(0,0,0,0.35)',
  pointerEvents: 'none',
  // The phone's gutter is narrower, and the labels move in with it.
  [theme.breakpoints.down('sm')]: { left: -44, width: 38, fontSize: 12.5 },
}));

/**
 * The mini day's labels, right-aligned a few pixels off the sky's edge and
 * hanging as far left as they need into whatever margin holds the day.
 */
const MiniHourLabel = styled('div')(({ theme }) => ({
  position: 'absolute',
  right: 'calc(100% + 9px)',
  transform: 'translateY(-52%)',
  fontFamily: FONTS.serif,
  fontWeight: 500,
  fontSize: 12,
  whiteSpace: 'nowrap',
  color: 'rgba(255,255,255,0.92)',
  pointerEvents: 'none',
  // A phone's margin is about forty pixels, "12 pm" included.
  [theme.breakpoints.down('sm')]: { right: 'calc(100% + 5px)', fontSize: 10.5 },
}));

function hourLabel(hour: number): string {
  if (hour === 0 || hour === 24) return '12 am';
  if (hour === 12) return '12 pm';
  return hour < 12 ? `${hour} am` : `${hour - 12} pm`;
}

/**
 * The painted sky, sized to the column. Absolutely positioned inside a track.
 * `mini`: square-cornered, since a mini day is a window cut into it.
 */
export function SkyBackdrop({ mini = false }: { mini?: boolean }) {
  return (
    <Frame aria-hidden="true" style={mini ? { borderRadius: 0 } : undefined}>
      {CLOUDS.map((c, i) => (
        <Cloud
          key={i}
          src="cloud.png"
          alt=""
          loading="lazy"
          style={{
            top: `${c.top}%`,
            left: `${c.left}%`,
            width: `${c.width}%`,
            opacity: c.opacity * CLOUD_OPACITY,
            transform: c.flip ? 'scaleX(-1)' : undefined,
          }}
        />
      ))}
    </Frame>
  );
}

/** Dashed hour rules across the column. */
export function HourGrid() {
  const hours = useMemo(() => {
    const out: number[] = [];
    for (let h = 5; h <= 24; h += 1) out.push(h);
    return out;
  }, []);

  return (
    <>
      {hours.map((h) => (
        <HourLine key={h} style={{ top: `${pct(h * 60)}%` }} aria-hidden="true" />
      ))}
    </>
  );
}

/**
 * Hour labels, rendered in the gutter beside the column.
 *
 * `within` is a mini day's window, in minutes: only the labels wholly inside it
 * are drawn, since a window centred two hours either side of an event usually
 * ends on the hour and would cut both end labels in half.
 */
export function HourGutter({ mini = false, within }: { mini?: boolean; within?: [number, number] }) {
  const hours = useMemo(() => {
    const out: number[] = [];
    for (let h = 5; h <= 24; h += 1) out.push(h);
    return out;
  }, []);

  const Label = mini ? MiniHourLabel : HourLabel;
  return (
    <>
      {hours
        .filter((h) => !within || (h * 60 > within[0] && h * 60 < within[1]))
        .map((h) => (
          <Label key={h} style={{ top: `${pct(h * 60)}%` }}>
            {hourLabel(h)}
          </Label>
        ))}
    </>
  );
}
